import { gql, useMutation, useQuery } from '@apollo/client';
import * as FullStory from '@fullstory/browser';
import posthog from 'posthog-js';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, Dropdown, DropdownItem } from 'reactstrap';
import styled from 'styled-components';
import { useState } from 'react';
import { useFarmSettingsContext } from '../../contexts/FarmSettingsContext';
import { useFarmSourceContext } from '../../contexts/FarmSourceContext';
import { CurrentUserQuery } from '../../queries';
import {
  useFarmName,
  useFarmUpdatedAt,
  useIsFarmView,
  useIsMultiProjectDashboardView,
  useIsPortfolioEvaluatorView,
  useToggleState,
  useUserFarmActions,
  useUserRole,
} from '../../utils/hooks';
import { mobileMediaQuery, useIsMobile } from '../../utils/responsive';
import FarmSettings from '../farm/FarmSettings';
import IconButton from '../basics/IconButton';
import Logo from '../icons/Logo';
import ShareFarmButton from '../ShareFarmButton';
import { ContextUnitToggle } from '../UnitToggle';
import { MODALS, useGlobalModal } from '../../contexts/GlobalModalsContext';
import SmallFontDropdownMenu from '../basics/SmallFontDropdownMenu';
import { NameInput } from '../left-menu/menu-components';
import useReadonly from '../../hooks/useReadonly';
import { UpdateFarmMutation } from '../../mutations';
import { showError } from '../../utils/toast';

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: space-between;
  width: 100%;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  h3 {
    margin: 0 0 0 var(--cell-padding);
    font-size: 1em;
    font-weight: bold;
  }
  padding: 0 var(--content-x-padding);
  @media ${mobileMediaQuery} {
    padding: 0.5rem var(--content-x-padding);
  }
  .dropdown-menu a {
    color: var(--text-color);
  }
  border-bottom: 0.5px solid var(--border-color);
`;

const LOG_OUT_MUTATION = gql`
  mutation LogOut {
    logOutUser(input: {}) {
      result
    }
  }
`;

const HeaderMainText = styled.div`
  text-align: left;
  font-size: 1em;
  color: var(--text-color);
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const HeaderSubtext = styled.div`
  text-align: left;
  font-size: 0.75em;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const setRootPath = () => {
  window.location.pathname = '/';
};

const AuthenticatedMenu = () => {
  const { farmId } = useParams();
  const isFarmView = useIsFarmView();
  const isDashboardView = useIsMultiProjectDashboardView();
  const isMobile = useIsMobile();
  const isPortfolioEvaluatorView = useIsPortfolioEvaluatorView();
  const location = useLocation();
  const { isSuperadmin } = useUserRole();
  const { canEdit } = useUserFarmActions(farmId);
  const { openSettingsModalForFarm } = useFarmSettingsContext();
  const { source } = useFarmSourceContext();
  const { setIsOpen } = useGlobalModal(MODALS.SHORTCUTS);
  const [logOut] = useMutation(LOG_OUT_MUTATION, {
    onCompleted: () => {
      posthog.reset();
      FullStory.anonymize(); // TODO: remove this when we're no longer using FullStory
      setRootPath();
    },
  });

  return (
    <SmallFontDropdownMenu positionFixed right>
      {(isFarmView || isDashboardView || isPortfolioEvaluatorView) && (
        <Link to={source || '/app'}>
          <DropdownItem>Back</DropdownItem>
        </Link>
      )}
      {isFarmView && (
        <>
          {(isSuperadmin || canEdit) && (
            <DropdownItem onClick={() => openSettingsModalForFarm(farmId)}>
              Project Settings
            </DropdownItem>
          )}
          <DropdownItem divider />
        </>
      )}
      {location.pathname === '/app/account' ? (
        <Link to="/app">
          <DropdownItem>Home</DropdownItem>
        </Link>
      ) : (
        <Link to="/app/account">
          <DropdownItem>User settings</DropdownItem>
        </Link>
      )}
      {isMobile && isSuperadmin && (
        <Link to="/superadmin/farms">
          <DropdownItem>Farms (Admin)</DropdownItem>
        </Link>
      )}
      <DropdownItem divider />
      <div className="px-3 py-1 align-self-center">
        <ContextUnitToggle buttonClassName="px-2 py-1" />
      </div>
      <DropdownItem divider />
      <DropdownItem onClick={() => setIsOpen(true)}>
        Keyboard Shortcuts
      </DropdownItem>
      <DropdownItem divider />
      <DropdownItem onClick={logOut}>Log out</DropdownItem>
    </SmallFontDropdownMenu>
  );
};

const UnauthenticatedMenu = () => {
  return (
    <SmallFontDropdownMenu positionFixed right>
      <div className="px-3 py-1 align-self-center">
        <ContextUnitToggle buttonClassName="px-2 py-1" />
      </div>
      <DropdownItem divider />
      <DropdownItem onClick={setRootPath}>Log in</DropdownItem>
    </SmallFontDropdownMenu>
  );
};

export const LogoDropdown = ({ user, buttonProps }) => {
  const isFarmView = useIsFarmView();
  const isDashboardView = useIsMultiProjectDashboardView();
  const isPortfolioEvaluatorView = useIsPortfolioEvaluatorView();
  const { isAuthenticated } = useUserRole();
  const isEditable = !useReadonly();
  const [dropdownOpen, toggleDropdownOpen] = useToggleState();
  const { farmId } = useParams();
  const farmName = useFarmName(!isFarmView);
  const farmUpdatedAt = useFarmUpdatedAt(!isFarmView);
  const [localName, setLocalName] = useState();
  const [updateFarmMutation] = useMutation(UpdateFarmMutation, {
    onCompleted: ({ updateFarm }) => {
      if (updateFarm.errors) {
        showError(updateFarm.errors);
        setLocalName(null);
      }
    },
    onError: (error) => {
      showError(error);
      setLocalName(null);
    },
  });
  const onClickName = (e) => {
    e.stopPropagation();
    if (dropdownOpen) toggleDropdownOpen();
  };

  return (
    <>
      <Dropdown
        id="logo-dropdown"
        isOpen={dropdownOpen}
        toggle={toggleDropdownOpen}
        className="flex-shrink-0 w-100"
      >
        <IconButton
          toggle
          size="sm"
          color="link"
          className="p-0 w-100"
          {...buttonProps}
        >
          <div className="d-flex align-items-center overflow-hidden">
            <Logo width={20} height={20} className="flex-shrink-0" />
            <div className="d-block px-3 w-100">
              {isFarmView ? (
                <NameInput
                  isEditable={isEditable}
                  name={localName || farmName}
                  onClick={onClickName}
                  onDoubleClick={onClickName}
                  renderDefault={() => (
                    <HeaderMainText>{localName || farmName}</HeaderMainText>
                  )}
                  update={(name) => {
                    if (name === farmName) return;
                    // Store updated name in state so that header doesn't switch
                    // back to old value before request completes
                    setLocalName(name);
                    updateFarmMutation({
                      variables: {
                        input: { id: farmId, name, updatedAt: farmUpdatedAt },
                      },
                    });
                  }}
                />
              ) : isDashboardView ? (
                <HeaderMainText>Multi Project Dashboard</HeaderMainText>
              ) : isPortfolioEvaluatorView ? (
                <HeaderMainText>Portfolio Evaluator</HeaderMainText>
              ) : (
                <>
                  <HeaderMainText>
                    {user?.me?.firstName} {user?.me?.lastName}
                  </HeaderMainText>
                  <HeaderSubtext>{user?.me?.email}</HeaderSubtext>
                </>
              )}
            </div>
          </div>
        </IconButton>
        {isAuthenticated ? <AuthenticatedMenu /> : <UnauthenticatedMenu />}
      </Dropdown>
      <FarmSettings />
    </>
  );
};

export const LogoNav = styled(({ className, user }) => {
  return (
    <HeaderBar className={className}>
      <HeaderContent>
        <LogoDropdown user={user} />
      </HeaderContent>
    </HeaderBar>
  );
})`
  border-right: 0.5px solid var(--border-color);
`;

const Header = ({ children, showFarmButtons = false }) => {
  const isMobile = useIsMobile();
  const { loading, data: user } = useQuery(CurrentUserQuery, {
    context: { priorityOp: true },
  });
  const { farmId } = useParams();
  const isUnauthenticated = !user?.me;
  const isSuperadmin = !!user?.me?.superadminRole;
  const { canShare } = useUserFarmActions(farmId);
  return (
    <>
      <LogoNav user={user} />
      {(children || !isMobile) && (
        <HeaderBar>
          <HeaderContent>{children}</HeaderContent>
          {showFarmButtons && (
            <div className="flex-shrink-0">
              {!loading && isUnauthenticated && (
                <Button
                  className="ml-2"
                  color="primary"
                  size="sm"
                  onClick={setRootPath}
                >
                  Log In
                </Button>
              )}
              {(isSuperadmin || canShare) && (
                <ShareFarmButton
                  farmId={farmId}
                  className="ml-2"
                  size="sm"
                  color="primary"
                />
              )}
            </div>
          )}
        </HeaderBar>
      )}
    </>
  );
};

export default Header;
